/**
 * ThemeStyleInjector.js
 * Injects custom CSS, background images, and manages sandboxed JavaScript
 */

import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useGameTheme } from '../../contexts/GameThemeContext';

/**
 * ThemeStyleInjector Component
 * Applies custom CSS, background images, and manages JavaScript sandbox
 */
export function ThemeStyleInjector({ children }) {
  const theme = useGameTheme();
  const styleElementRef = useRef(null);
  const sandboxRef = useRef(null);

  // Inject custom CSS
  useEffect(() => {
    if (!theme?.customCSS) return;

    // Sanitize CSS: cap z-index on pseudo-elements and ensure pointer-events: none on overlays
    let sanitizedCSS = theme.customCSS;
    // Replace dangerously high z-index values on body::before/::after with safe values
    sanitizedCSS = sanitizedCSS.replace(/(body\s*::(?:before|after)\s*\{[^}]*?)z-index\s*:\s*\d+/gi, '$1z-index: -1');
    // Ensure all fixed position pseudo-element overlays don't block clicks
    sanitizedCSS += '\nbody::before, body::after { pointer-events: none !important; }';
    // 🛡️ Diegetic prop-UI shield (Nina 08-13, Prompt Pals shelf): generated
    // themes love blanket `button { background: … !important }` rules, which
    // beat inline styles and repaint wood-prop surfaces — the shelf's emoji
    // tiles and close ✕ wore the theme's purple portal gradient. Buttons that
    // stamp data-prop-ui get their own background back: this rule lands AFTER
    // the generated CSS so it wins the cascade at equal importance (doubled
    // attribute = specificity bump over `button:not(x):not(y)`), and a button
    // with a real background supplies it via --prop-bg (unset ⇒ none, the
    // shelf-tile default). Background only on purpose: nothing observed mints
    // !important borders/shadows, and a border shorthand here would stomp
    // per-side inline borders (the shelf edge tab's borderRight: none).
    sanitizedCSS += '\nbutton[data-prop-ui][data-prop-ui], button[data-prop-ui][data-prop-ui]:hover {'
      + ' background: var(--prop-bg, none) !important; }';

    // Create style element
    const styleElement = document.createElement('style');
    styleElement.setAttribute('data-game-theme', 'custom');
    styleElement.textContent = sanitizedCSS;
    document.head.appendChild(styleElement);
    styleElementRef.current = styleElement;

    return () => {
      if (styleElementRef.current) {
        styleElementRef.current.remove();
      }
    };
  }, [theme?.customCSS]);

  // Apply background image to body element.
  // The URL alone decides: paint whenever one exists. `enabled` is deliberately
  // IGNORED here — every legitimate disable path (generation failed after max
  // attempts) leaves url unset, while ThemeValidator.validateBackgroundImage
  // historically demoted enabled to false on re-validation of a merged theme
  // WHILE KEEPING the url, persisting url+enabled:false and blanking the body
  // ("background turns black after it generated", Nina 07-25). url ⇒ paint
  // closes that whole class at the single choke point. See
  // BG_WALLPAPER_BLACKOUT_HANDOFF_2026_07_25.md for the full mechanism.
  useEffect(() => {
    if (!theme?.backgroundImage?.url) {
      // Nothing to paint (never generated, or generation failed → url stays unset)
      document.body.style.backgroundImage = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundPosition = '';
      document.body.style.backgroundRepeat = '';
      document.body.style.backgroundAttachment = '';
      return;
    }

    // Apply tileable background pattern to body.
    // 🚪🖼️ --room-wallpaper-veil (09-15, /room Tier 0): the body wallpaper is
    // the ONE layer genuinely behind the page (even z:-1 elements paint over
    // it — GameStepPage's stacking note), so the /room camera dims/tints it
    // by composing a veil as the FIRST background layer. GameStepPage owns
    // the CSS var (set on documentElement per room, removed for the main
    // view); this injector stays the sole writer of body.style, so the two
    // never race on one property. Unset var ⇒ the transparent fallback ⇒
    // byte-identical paint for every non-house space. The veil must stay
    // FLAT (two identical stops): background-size 500px applies to every
    // layer, so any shaped gradient would tile as stripes (pinned in
    // roomView.test.js).
    // 🖼️ 09-17 (art as wallpaper): two more var-gated layers between the
    // veil and the tile — `--room-wallpaper-dim` (a SHAPED gradient: clear
    // header zone, dimmed under the chat) and `--room-wallpaper-art` (the
    // room's real art, cover). They get their OWN size/repeat slots below
    // (100% 100% / cover, no-repeat) precisely so shape is legal there; the
    // veil and the tile keep 500px repeat. All three vars unset ⇒ three
    // transparent layers ⇒ the same paint as before for every other space.
    const CLEAR = 'linear-gradient(rgba(0,0,0,0), rgba(0,0,0,0))';
    document.body.style.backgroundImage = `var(--room-wallpaper-veil, ${CLEAR}), var(--room-wallpaper-dim, ${CLEAR}), var(--room-wallpaper-art, ${CLEAR}), url(${theme.backgroundImage.url})`;
    document.body.style.backgroundSize = '500px 500px, 100% 100%, cover, 500px 500px';
    document.body.style.backgroundPosition = 'center';
    document.body.style.backgroundRepeat = 'repeat, no-repeat, no-repeat, repeat';
    document.body.style.backgroundAttachment = 'fixed';

    return () => {
      // Clean up on unmount
      document.body.style.backgroundImage = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundPosition = '';
      document.body.style.backgroundRepeat = '';
      document.body.style.backgroundAttachment = '';
    };
  }, [theme?.backgroundImage?.enabled, theme?.backgroundImage?.url]);

  // Apply CSS custom properties for theme colors
  useEffect(() => {
    if (!theme?.colors) {
      console.log('[ThemeStyleInjector] No colors in theme');
      return;
    }

    console.log('[ThemeStyleInjector] Applying CSS custom properties:', theme.colors);

    const root = document.documentElement;
    const colors = theme.colors;

    // Set CSS variables
    root.style.setProperty('--game-theme-primary', colors.primary);
    root.style.setProperty('--game-theme-secondary', colors.secondary);
    root.style.setProperty('--game-theme-background', colors.background);
    root.style.setProperty('--game-theme-surface', colors.surface);
    root.style.setProperty('--game-theme-text-primary', colors.textPrimary);
    root.style.setProperty('--game-theme-text-secondary', colors.textSecondary);
    root.style.setProperty('--game-theme-success', colors.success);
    root.style.setProperty('--game-theme-warning', colors.warning);
    root.style.setProperty('--game-theme-error', colors.error);
    root.style.setProperty('--game-theme-overlay', colors.overlay);

    return () => {
      // Clean up CSS variables
      root.style.removeProperty('--game-theme-primary');
      root.style.removeProperty('--game-theme-secondary');
      root.style.removeProperty('--game-theme-background');
      root.style.removeProperty('--game-theme-surface');
      root.style.removeProperty('--game-theme-text-primary');
      root.style.removeProperty('--game-theme-text-secondary');
      root.style.removeProperty('--game-theme-success');
      root.style.removeProperty('--game-theme-warning');
      root.style.removeProperty('--game-theme-error');
      root.style.removeProperty('--game-theme-overlay');
    };
  }, [theme?.colors]);

  // Initialize event transitions + ambient JS sandbox
  // All JavaScript (ambient effects + event-triggered transitions) is generated together in Phase 3
  // Disabled on mobile to avoid covering interactive UI
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (!theme?.eventTransitions || isMobile) return;

    console.log('[ThemeStyleInjector] Initializing event transitions + ambient JS execution');

    try {
      const sandbox = new JavaScriptSandbox(theme.eventTransitions);
      sandbox.initialize();
      sandboxRef.current = sandbox;

      return () => {
        if (sandboxRef.current) {
          sandboxRef.current.cleanup();
          sandboxRef.current = null;
        }
      };
    } catch (error) {
      console.error('[ThemeStyleInjector] Failed to initialize event transitions sandbox:', error);
    }
  }, [theme?.eventTransitions]);

  // Render children only (background applied directly to body element)
  // Banner is rendered in GameStepPage sidebar
  return <>{children}</>;
}

/**
 * JavaScriptSandbox
 * Safely executes custom JavaScript in an isolated iframe
 */
class JavaScriptSandbox {
  constructor(code) {
    this.code = code;
    this.iframe = null;
  }

  initialize() {
    // Create sandbox iframe
    this.iframe = document.createElement('iframe');
    // Allow same-origin so iframe can access parent document for theme manipulation
    // The code is already sanitized by ThemeValidator on the backend
    this.iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
    // Hidden but SIZED (not display:none): a display:none iframe has a 0x0
    // viewport, so generated ambient code reading window.innerWidth/Height
    // computes NaN geometry — createRadialGradient then throws non-finite
    // and the whole ambient script dies (yummo gumbo, 07-26). visibility:
    // hidden keeps rAF + real dimensions without painting or catching
    // pointer events.
    this.iframe.style.cssText =
      'position:fixed;top:0;left:0;width:100%;height:100%;' +
      'visibility:hidden;pointer-events:none;border:0;';
    document.body.appendChild(this.iframe);

    // Prepare safe code execution environment
    const safeCode = this.prepareSafeCode(this.code);

    // Execute code in sandbox
    try {
      const iframeDoc = this.iframe.contentDocument || this.iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <style>
              body { margin: 0; padding: 0; }
            </style>
          </head>
          <body>
            <script>
              try {
                // Provide minimal safe API
                const parentDocument = window.parent.document;
                const parentWindow = window.parent;

                // Wrap addEventListener to catch errors in event handlers
                const _origAddEventListener = parentDocument.addEventListener.bind(parentDocument);
                const safeAddEventListener = (event, handler, options) => {
                  _origAddEventListener(event, (e) => {
                    try { handler(e); }
                    catch (err) { console.error('[ThemeEvent] Error in ' + event + ' handler:', err); }
                  }, options);
                };
                // Canvas gradients THROW on non-finite args (arc etc. just
                // no-op), and generated particle code trips this whenever a
                // per-type field is missing (e.g. ember particles without
                // wobbleSpeed -> time*undefined -> NaN x -> createRadialGradient
                // throws and the WHOLE ambient script dies — yummo gumbo,
                // 07-26). Guard only canvases the THEME code creates: coerce
                // non-finite gradient args to 0 (degenerate gradient = draws
                // nothing, animation survives). Parent app canvases untouched.
                const guardCanvas = (el) => {
                  const origGetContext = el.getContext.bind(el);
                  el.getContext = (type, ...rest) => {
                    const ctx = origGetContext(type, ...rest);
                    if (ctx && type === '2d') {
                      ['createRadialGradient', 'createLinearGradient', 'createConicGradient'].forEach((m) => {
                        if (typeof ctx[m] === 'function') {
                          const orig = ctx[m].bind(ctx);
                          ctx[m] = (...args) => orig(...args.map((v) => {
                            const n = Number(v);
                            return Number.isFinite(n) ? n : 0;
                          }));
                        }
                      });
                    }
                    return ctx;
                  };
                  return el;
                };

                // Override parentDocument.addEventListener for theme code
                const _parentDocument = new Proxy(parentDocument, {
                  get(target, prop) {
                    if (prop === 'addEventListener') return safeAddEventListener;
                    if (prop === 'createElement') {
                      return (tag, ...rest) => {
                        const el = target.createElement(tag, ...rest);
                        return String(tag).toLowerCase() === 'canvas' ? guardCanvas(el) : el;
                      };
                    }
                    const val = target[prop];
                    return typeof val === 'function' ? val.bind(target) : val;
                  }
                });

                // Execute custom code with safe parentDocument
                (function(parentDocument) {
                  ${safeCode}
                })(_parentDocument);
              } catch (error) {
                console.error('[ThemeSandbox] Execution error:', error);
              }
            </script>
          </body>
        </html>
      `);
      iframeDoc.close();
    } catch (error) {
      console.error('Failed to execute sandboxed JavaScript:', error);
      this.cleanup();
    }
  }

  prepareSafeCode(code) {
    let safeCode = code;

    // Strip markdown code fences and language markers that leaked from AI responses
    // Handle bare "javascript" or "js" at the very start (from partially stripped fences)
    safeCode = safeCode.replace(/^(?:javascript|js)\s*\n/, '');
    // Handle full fences: ```javascript ... ```
    const fenceMatch = safeCode.match(/```(?:javascript|js)?\s*\n([\s\S]*?)```/);
    if (fenceMatch) {
      safeCode = fenceMatch[1];
    }
    // Remove any remaining stray fence lines
    safeCode = safeCode.replace(/^```(?:javascript|js)?\s*$/gm, '').replace(/^```\s*$/gm, '');

    // Remove dangerous window/parent access patterns
    safeCode = safeCode.replace(/window\.parent\.parent/g, '/* blocked */');
    safeCode = safeCode.replace(/window\.top/g, '/* blocked */');
    safeCode = safeCode.replace(/window\.frameElement/g, '/* blocked */');

    // Normalize bare hex literals in addColorStop calls. AI-generated theme
    // code sometimes emits addColorStop(0, 'F15BB5') without the '#' prefix,
    // which throws SyntaxError and kills the ambient animation. Hex with '#'
    // is unaffected because '#' isn't in the [0-9a-fA-F] char class.
    safeCode = safeCode.replace(
      /\baddColorStop\s*\(\s*([^,]+?)\s*,\s*(['"])([0-9a-fA-F]{3,8})\2\s*\)/g,
      (_match, stop, quote, hex) => `addColorStop(${stop}, ${quote}#${hex}${quote})`
    );

    return safeCode.trim();
  }

  cleanup() {
    if (this.iframe && this.iframe.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
      this.iframe = null;
    }
  }
}

export default ThemeStyleInjector;
